import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const externalErrorTypeValues = [
  "MISSING_PHONE_NUMBER",
  "UNABLE_TO_FETCH_ADDRESS_DETAILS",
  "NO_PHONE_FOUND_FOR_FROM_NUMBER",
  "NO_PHONE_FOUND_FOR_PERSON",
  "MISSING_SCHEDULED_DATE",
  "CONVERSATION_ITEM_NOT_FOUND",
  "NO_PHONE_FOUND_FOR_SMS",
  "NO_BID_FOUND_FOR_SMS",
  "NO_TEXT_FOUND_FOR_SMS",
  "NO_SMS_MESSAGE_FOUND_FOR_CONVERSATION_ITEM",
  "NO_MESSAGE_ID_FOUND_FOR_SMS_MESSAGE",
  "PANDADOC_DOCUMENT_NOT_READY_TO_BE_SENT",
  "PANDADOC_DOCUMENT_SEND_IN_PROGRESS",
  "FAILED_TO_CREATE_DOCUMENT_FROM_TEMPLATE",
  "REQUEST_FOR_QUOTE_ID_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "CANT_CREATE_DOCUMENT_WITHOUT_HOMEOWNER_EMAIL",
  "MISSING_INSURANCE_QUOTE_DATA_FOR_HIC",
  "HOMEOWNER_HAS_NO_PHONE_NUMBER",
  "INVALID_HOMEOWNER_PHONE_NUMBER",
  "INVALID_CO_APPLICANT_PHONE_NUMBER",
  "CONTRACTOR_LICENSE_NUMBER_NOT_FOUND",
  "NO_CONTEXT_USER_EMAIL_FOUND",
  "PANDADOC_DOCUMENT_NOT_FOUND",
  "PANDADOC_DOCUMENT_TYPE_MISMATCH",
  "PANDADOC_DOCUMENT_NOT_AWAITING_SIGNATURE",
  "PANDADOC_DOCUMENT_HAS_NO_RECIPIENT",
  "BID_REQUEST_NOT_FOUND",
  "WEBHOOKS_ARE_CONFIGURED_TO_SEND_ON_SEND_OR_ON_COMPLETE",
  "PROJECT_NOT_FOUND_FOR_DOCUMENT_ID",
  "FAILED_TO_FIND_SALES_OWNER_FOR_QUOTE",
  "ERROR_HANDLING_DOCUMENT_COMPLETION",
  "LESS_THAN_TWO_FILES_FOUND_IN_THE_QUOTE_AND_HIC_ZIP_ARCHIVE",
  "HIC_PDF_NOT_FOUND_IN_ZIP_ARCHIVE",
  "PANDADOC_SIGNED_ASSETS_NOT_READY",
  "EAGLEVIEW_REPORT_IS_EMPTY",
  "EAGLEVIEW_EMPTY_REPORT_IDS",
  "EAGLEVIEW_NO_AVAILABLE_PRODUCTS",
  "EAGLEVIEW_SWEEP_BID_NOT_FOUND",
  "EAGLEVIEW_SWEEP_INGESTION_FAILED",
  "EAGLEVIEW_DUPLICATE_ORIGINAL_INGEST_FAILED",
  "PROJECT_NOT_FOUND",
  "FAILED_TO_UPLOAD_FILES",
  "FAILED_TO_GET_ADDRESS_DETAILS",
  "ORGANIZATION_ID_REQUIRED",
  "FAILED_TO_CREATE_ASSIGNMENT",
  "INSURANCE_BID_HAS_MULTIPLE_RFQS",
  "BID_NOT_FOUND",
  "STATE_UNLICENSED",
  "FINANCING_NOT_FOUND",
  "QUOTE_VERSION_NOT_FOUND",
  "PERSON_NOT_FOUND",
  "CONVERSATION_NOT_FOUND",
  "PHONE_NOT_FOUND",
  "SMS_MESSAGE_NOT_FOUND",
  "FILE_NOT_FOUND",
  "CLAIM_NUMBER_NOT_FOUND",
  "INVALID_PHONE_CALL_QUEUE_OPERATION",
  "ADDRESS_REQUIRED",
  "ADDRESS_NOT_FOUND",
  "CERTIFICATE_OF_COMPLETION_NOT_FOUND",
  "CERTIFICATE_OF_COMPLETION_TASK_MISSING",
  "MISSING_REQUIRED_FIELDS",
  "ORGANIZATION_ID_NOT_SUPPORTED",
  "SUNRUN_INTEGRATION_SERVER_URL_NOT_CONFIGURED",
  "SUNRUN_INTEGRATION_WEBHOOK_SECRET_NOT_CONFIGURED",
  "RDCL_API_HOST_NOT_CONFIGURED",
  "BID_PROJECT_ID_NOT_FOUND",
  "HIC_COMPLETED_ON_NON_ACTIVE_QUOTE",
  "MISSING_QUOTE_IDENTIFIER",
  "UNSUPPORTED_PARTNER_FLOW",
  "SMS_UPDATE_FAILED_AFTER_SEND",
  "PANDADOC_COMPLETION_EMIT_FAILED",
  "QUOTE_ESTIMATE_NOT_FOUND",
] as const;
const externalErrorTypeSchema = z.enum(externalErrorTypeValues);
const ExternalErrorType = externalErrorTypeSchema.enum;
type ExternalErrorType = z.infer<typeof externalErrorTypeSchema>;

export type ExternalErrors =
  | { errorType: typeof ExternalErrorType.BID_NOT_FOUND; data: { bidId?: number; projectId?: number } }
  | {
      errorType: typeof ExternalErrorType.MISSING_PHONE_NUMBER;
      data: { bidId: number; email?: string; phone?: string };
    }
  | {
      errorType: typeof ExternalErrorType.UNABLE_TO_FETCH_ADDRESS_DETAILS;
      data: { placeId: string; addressString?: string };
    }
  | {
      errorType: typeof ExternalErrorType.NO_PHONE_FOUND_FOR_FROM_NUMBER;
      data: { projectId: number; fromPhoneNumber: string };
    }
  | { errorType: typeof ExternalErrorType.NO_PHONE_FOUND_FOR_PERSON; data: { projectId?: number; personId: string } }
  | { errorType: typeof ExternalErrorType.MISSING_SCHEDULED_DATE; data: undefined }
  | {
      errorType: typeof ExternalErrorType.CONVERSATION_ITEM_NOT_FOUND;
      data: { smsId?: string; conversationItemId?: string };
    }
  | { errorType: typeof ExternalErrorType.NO_PHONE_FOUND_FOR_SMS; data: { smsId: string } }
  | { errorType: typeof ExternalErrorType.NO_BID_FOUND_FOR_SMS; data: { smsId: string } }
  | { errorType: typeof ExternalErrorType.NO_TEXT_FOUND_FOR_SMS; data: { smsId: string } }
  | {
      errorType: typeof ExternalErrorType.NO_SMS_MESSAGE_FOUND_FOR_CONVERSATION_ITEM;
      data: { conversationItemId: string };
    }
  | { errorType: typeof ExternalErrorType.NO_MESSAGE_ID_FOUND_FOR_SMS_MESSAGE; data: { smsMessageId: string } }
  | { errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_NOT_FOUND; data: { documentId: string } }
  | {
      errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_TYPE_MISMATCH;
      data: { documentId: string; expected: string; received: string };
    }
  | {
      errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_NOT_AWAITING_SIGNATURE;
      data: { documentId: string; documentStatus: string };
    }
  | { errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_HAS_NO_RECIPIENT; data: { documentId: string } }
  | {
      errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_NOT_READY_TO_BE_SENT;
      data: { documentId: string; status?: string };
    }
  | {
      errorType: typeof ExternalErrorType.PANDADOC_DOCUMENT_SEND_IN_PROGRESS;
      data: { bidId: number; documentType: string };
    }
  | {
      errorType: typeof ExternalErrorType.FAILED_TO_CREATE_DOCUMENT_FROM_TEMPLATE;
      data: { quoteVersionId: string };
    }
  | { errorType: typeof ExternalErrorType.REQUEST_FOR_QUOTE_ID_NOT_FOUND; data: { quoteVersionId: string } }
  | {
      errorType: typeof ExternalErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: { bidId: number; partnerFlow: string };
    }
  | {
      errorType: typeof ExternalErrorType.CANT_CREATE_DOCUMENT_WITHOUT_HOMEOWNER_EMAIL;
      data: { quoteVersionId: string };
    }
  | {
      errorType: typeof ExternalErrorType.MISSING_INSURANCE_QUOTE_DATA_FOR_HIC;
      data: { quoteVersionId: string; lossDate?: Date | null; claimNumber?: string | null };
    }
  | { errorType: typeof ExternalErrorType.HOMEOWNER_HAS_NO_PHONE_NUMBER; data: { quoteVersionId: string } }
  | {
      errorType: typeof ExternalErrorType.INVALID_HOMEOWNER_PHONE_NUMBER;
      data: { quoteVersionId: string; phoneNumber?: string };
    }
  | {
      errorType: typeof ExternalErrorType.INVALID_CO_APPLICANT_PHONE_NUMBER;
      data: { quoteVersionId: string; phoneNumber?: string };
    }
  | {
      errorType: typeof ExternalErrorType.CONTRACTOR_LICENSE_NUMBER_NOT_FOUND;
      data: { stateCode: string; organizationId: string };
    }
  | { errorType: typeof ExternalErrorType.NO_CONTEXT_USER_EMAIL_FOUND; data: { personId: string } }
  | {
      errorType: typeof ExternalErrorType.BID_REQUEST_NOT_FOUND;
      data: { documentId?: string; bidRequestId?: string; requestForQuoteId?: number };
    }
  | {
      errorType: typeof ExternalErrorType.WEBHOOKS_ARE_CONFIGURED_TO_SEND_ON_SEND_OR_ON_COMPLETE;
      data: { documentId: string };
    }
  | { errorType: typeof ExternalErrorType.PROJECT_NOT_FOUND_FOR_DOCUMENT_ID; data: { documentId: string } }
  | { errorType: typeof ExternalErrorType.FAILED_TO_FIND_SALES_OWNER_FOR_QUOTE; data: { quoteVersionId: string } }
  | {
      errorType: typeof ExternalErrorType.ERROR_HANDLING_DOCUMENT_COMPLETION;
      data: { documentId: string; bidProjectId: number; reason: string };
    }
  | { errorType: typeof ExternalErrorType.HIC_PDF_NOT_FOUND_IN_ZIP_ARCHIVE; data: { documentId: string } }
  | { errorType: typeof ExternalErrorType.PANDADOC_SIGNED_ASSETS_NOT_READY; data: { documentId: string } }
  | {
      errorType: typeof ExternalErrorType.LESS_THAN_TWO_FILES_FOUND_IN_THE_QUOTE_AND_HIC_ZIP_ARCHIVE;
      data: { documentId: string };
    }
  | {
      errorType: typeof ExternalErrorType.EAGLEVIEW_REPORT_IS_EMPTY;
      data: { projectId: number; eagleViewReportId: string };
    }
  | {
      errorType: typeof ExternalErrorType.EAGLEVIEW_EMPTY_REPORT_IDS;
      data: { projectId: number };
    }
  | { errorType: typeof ExternalErrorType.EAGLEVIEW_NO_AVAILABLE_PRODUCTS; data: Record<string, never> }
  | {
      errorType: typeof ExternalErrorType.EAGLEVIEW_DUPLICATE_ORIGINAL_INGEST_FAILED;
      data: { projectId: number; duplicateReportId: string; originalReportId: string; ingestionStatus: string | null };
    }
  | {
      errorType: typeof ExternalErrorType.EAGLEVIEW_SWEEP_BID_NOT_FOUND;
      data: { reportId: string; bidRequestId: string } | { reportId: string; bidProjectId: number };
    }
  | {
      errorType: typeof ExternalErrorType.EAGLEVIEW_SWEEP_INGESTION_FAILED;
      data: { reportId: string; projectId: number; ingestionStatus: string };
    }
  | { errorType: typeof ExternalErrorType.PROJECT_NOT_FOUND; data: { projectId?: number } }
  | {
      errorType: typeof ExternalErrorType.FAILED_TO_UPLOAD_FILES;
      data: {
        assignmentId: string;
        failCount: number;
        successCount: number;
        totalFiles: number;
        failedFileIds: string[];
      };
    }
  | {
      errorType: typeof ExternalErrorType.FAILED_TO_GET_ADDRESS_DETAILS;
      data: { addressId: string; addressString: string };
    }
  | { errorType: typeof ExternalErrorType.ORGANIZATION_ID_REQUIRED; data: { organizationId: string } }
  | { errorType: typeof ExternalErrorType.FAILED_TO_CREATE_ASSIGNMENT; data: { assignmentId: string } }
  | {
      errorType: typeof ExternalErrorType.INSURANCE_BID_HAS_MULTIPLE_RFQS;
      data: { bidId: number; partnerFlow: string };
    }
  | {
      errorType: typeof ExternalErrorType.STATE_UNLICENSED;
      data: { state: string };
    }
  | { errorType: typeof ExternalErrorType.FINANCING_NOT_FOUND; data: { requestForQuoteId: number } }
  | { errorType: typeof ExternalErrorType.QUOTE_VERSION_NOT_FOUND; data: { quoteVersionId: string } }
  | { errorType: typeof ExternalErrorType.PERSON_NOT_FOUND; data: { personId?: string } }
  | { errorType: typeof ExternalErrorType.CONVERSATION_NOT_FOUND; data: { id: string } }
  | { errorType: typeof ExternalErrorType.PHONE_NOT_FOUND; data: { projectId?: number; phoneId: string } }
  | { errorType: typeof ExternalErrorType.SMS_MESSAGE_NOT_FOUND; data: { smsId: string } }
  | { errorType: typeof ExternalErrorType.FILE_NOT_FOUND; data: { fileId: string } }
  | { errorType: typeof ExternalErrorType.CLAIM_NUMBER_NOT_FOUND; data: { externalId: string } }
  | {
      errorType: typeof ExternalErrorType.INVALID_PHONE_CALL_QUEUE_OPERATION;
      data: { operation: string; queueName: string; provider?: string };
    }
  | { errorType: typeof ExternalErrorType.ADDRESS_REQUIRED; data: { address?: string } }
  | { errorType: typeof ExternalErrorType.ADDRESS_NOT_FOUND; data: { address?: string } }
  | { errorType: typeof ExternalErrorType.CERTIFICATE_OF_COMPLETION_NOT_FOUND; data: { quoteVersionId: string } }
  | {
      errorType: typeof ExternalErrorType.CERTIFICATE_OF_COMPLETION_TASK_MISSING;
      data: { projectId: number; documentId: string };
    }
  | {
      errorType: typeof ExternalErrorType.MISSING_REQUIRED_FIELDS;
      data: { input: Record<string, unknown> };
    }
  | {
      errorType: typeof ExternalErrorType.MISSING_QUOTE_IDENTIFIER;
      data: { documentId: string };
    }
  | {
      errorType: typeof ExternalErrorType.ORGANIZATION_ID_NOT_SUPPORTED;
      data: { organizationId: string };
    }
  | {
      errorType: typeof ExternalErrorType.SUNRUN_INTEGRATION_SERVER_URL_NOT_CONFIGURED;
      data: { projectId: number; submissionId: string };
    }
  | {
      errorType: typeof ExternalErrorType.SUNRUN_INTEGRATION_WEBHOOK_SECRET_NOT_CONFIGURED;
      data: { projectId: number; submissionId: string };
    }
  | {
      errorType: typeof ExternalErrorType.RDCL_API_HOST_NOT_CONFIGURED;
      data: undefined;
    }
  | {
      errorType: typeof ExternalErrorType.BID_PROJECT_ID_NOT_FOUND;
      data: { documentId: string };
    }
  | {
      errorType: typeof ExternalErrorType.HIC_COMPLETED_ON_NON_ACTIVE_QUOTE;
      data: {
        documentId: string;
        hicQuoteId: string | null;
        activeQuoteId: string | null;
        pendingActiveQuoteVersionId?: string | null;
        bidProjectId: number;
      };
    }
  | {
      errorType: typeof ExternalErrorType.UNSUPPORTED_PARTNER_FLOW;
      data: { projectId: number; partnerFlow: string };
    }
  | {
      errorType: typeof ExternalErrorType.PANDADOC_COMPLETION_EMIT_FAILED;
      data: { documentId: string; originalErrorName: string; originalErrorMessage: string };
    }
  | {
      errorType: typeof ExternalErrorType.SMS_UPDATE_FAILED_AFTER_SEND;
      data: { smsId: string; placeholderExternalId: string; sentExternalId?: string; provider: string };
    }
  | { errorType: typeof ExternalErrorType.QUOTE_ESTIMATE_NOT_FOUND; data: { quoteVersionId: string } };

export const externalErrorDetails: Record<ExternalErrorType, ErrorDetails> = {
  MISSING_PHONE_NUMBER: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Missing phone number",
  },
  UNABLE_TO_FETCH_ADDRESS_DETAILS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unable to fetch address details",
  },
  NO_PHONE_FOUND_FOR_FROM_NUMBER: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No phone found for from number",
  },
  NO_PHONE_FOUND_FOR_PERSON: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No phone found for person",
  },
  MISSING_SCHEDULED_DATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Missing scheduled date",
  },
  CONVERSATION_ITEM_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation item not found",
  },
  NO_PHONE_FOUND_FOR_SMS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No phone found for sms",
  },
  NO_BID_FOUND_FOR_SMS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid found for sms",
  },
  NO_TEXT_FOUND_FOR_SMS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No text found for sms",
  },
  NO_SMS_MESSAGE_FOUND_FOR_CONVERSATION_ITEM: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No sms message found for conversation item",
  },
  NO_MESSAGE_ID_FOUND_FOR_SMS_MESSAGE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No message id found for sms message",
  },
  PANDADOC_DOCUMENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Pandadoc document not found",
  },
  PANDADOC_DOCUMENT_TYPE_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Pandadoc document type mismatch between stored and requested",
  },
  PANDADOC_DOCUMENT_NOT_AWAITING_SIGNATURE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Pandadoc document is not awaiting a signature",
  },
  PANDADOC_DOCUMENT_HAS_NO_RECIPIENT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Pandadoc document has no stored recipient",
  },
  PANDADOC_DOCUMENT_NOT_READY_TO_BE_SENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Pandadoc document not ready to be sent",
  },
  PANDADOC_DOCUMENT_SEND_IN_PROGRESS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Pandadoc document is already being sent for this bid",
  },
  FAILED_TO_CREATE_DOCUMENT_FROM_TEMPLATE: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to create document from template",
  },
  REQUEST_FOR_QUOTE_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote id not found",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  CANT_CREATE_DOCUMENT_WITHOUT_HOMEOWNER_EMAIL: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Can't create document without homeowner email",
  },
  MISSING_INSURANCE_QUOTE_DATA_FOR_HIC: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Missing insurance quote data required for HIC creation",
  },
  HOMEOWNER_HAS_NO_PHONE_NUMBER: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Homeowner has no phone number",
  },
  INVALID_HOMEOWNER_PHONE_NUMBER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Homeowner phone number is invalid or missing",
  },
  INVALID_CO_APPLICANT_PHONE_NUMBER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Co-applicant phone number is invalid or missing",
  },
  CONTRACTOR_LICENSE_NUMBER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor license number not found",
  },
  NO_CONTEXT_USER_EMAIL_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No context user email found",
  },
  BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid request not found",
  },
  WEBHOOKS_ARE_CONFIGURED_TO_SEND_ON_SEND_OR_ON_COMPLETE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Webhooks are configured to send on send or on complete",
  },
  PROJECT_NOT_FOUND_FOR_DOCUMENT_ID: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found for document id",
  },
  FAILED_TO_FIND_SALES_OWNER_FOR_QUOTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Failed to find sales owner for quote",
  },
  ERROR_HANDLING_DOCUMENT_COMPLETION: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Error handling document completion",
  },
  HIC_PDF_NOT_FOUND_IN_ZIP_ARCHIVE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "HIC PDF not found in zip archive",
  },
  PANDADOC_SIGNED_ASSETS_NOT_READY: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "PandaDoc signed download assets are still being produced",
  },
  LESS_THAN_TWO_FILES_FOUND_IN_THE_QUOTE_AND_HIC_ZIP_ARCHIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Less than two files found in the quote and HIC zip archive",
  },
  EAGLEVIEW_REPORT_IS_EMPTY: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Eagleview report is empty",
  },
  EAGLEVIEW_DUPLICATE_ORIGINAL_INGEST_FAILED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription:
      "EagleView duplicate's original report could not be ingested; bid returned to the closed duplicate",
  },
  EAGLEVIEW_EMPTY_REPORT_IDS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "EagleView PlaceOrder returned empty ReportIds",
  },
  EAGLEVIEW_NO_AVAILABLE_PRODUCTS: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "EagleView GetAvailableProducts returned no products",
  },
  EAGLEVIEW_SWEEP_BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription:
      "EagleView sweep matched a report but the bid data row could not be resolved (by claim number or reference id)",
  },
  EAGLEVIEW_SWEEP_INGESTION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "EagleView sweep failed to ingest an attached report",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  FAILED_TO_UPLOAD_FILES: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to upload files",
  },
  FAILED_TO_GET_ADDRESS_DETAILS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Failed to get address details",
  },
  ORGANIZATION_ID_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Organization ID is required",
  },
  FAILED_TO_CREATE_ASSIGNMENT: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to create assignment",
  },
  INSURANCE_BID_HAS_MULTIPLE_RFQS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Insurance bid has multiple RFQs",
  },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  STATE_UNLICENSED: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "State unlicensed" },
  FINANCING_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Financing not found",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found",
  },
  PERSON_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person not found",
  },
  CONVERSATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation not found",
  },
  PHONE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Phone not found",
  },
  SMS_MESSAGE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "SMS message not found",
  },
  FILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File not found",
  },
  CLAIM_NUMBER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Insurance bid request has no claim number",
  },
  INVALID_PHONE_CALL_QUEUE_OPERATION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid operation type in phone call queue processing",
  },
  ADDRESS_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Address is required",
  },
  ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Address not found",
  },
  CERTIFICATE_OF_COMPLETION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Certificate of completion not found",
  },
  CERTIFICATE_OF_COMPLETION_TASK_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Certificate of completion task missing (soft-deleted or absent); document completion continued",
  },
  MISSING_REQUIRED_FIELDS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Missing required fields",
  },
  MISSING_QUOTE_IDENTIFIER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "No quote identifier was provided",
  },
  ORGANIZATION_ID_NOT_SUPPORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Organization ID not supported",
  },
  SUNRUN_INTEGRATION_SERVER_URL_NOT_CONFIGURED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SUNRUN_INTEGRATION_SERVER_URL environment variable is not configured",
  },
  SUNRUN_INTEGRATION_WEBHOOK_SECRET_NOT_CONFIGURED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SUNRUN_INTEGRATION_WEBHOOK_SECRET environment variable is not configured",
  },
  RDCL_API_HOST_NOT_CONFIGURED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "RDCL_API_HOST environment variable is not configured",
  },
  BID_PROJECT_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid project id not found",
  },
  HIC_COMPLETED_ON_NON_ACTIVE_QUOTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC completed on a non-active quote",
  },
  UNSUPPORTED_PARTNER_FLOW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Partner flow does not support this operation",
  },
  SMS_UPDATE_FAILED_AFTER_SEND: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "SMS status update failed after the message was sent",
  },
  PANDADOC_COMPLETION_EMIT_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Emitting pandadoc/document-completion.requested to Inngest failed; the completion fell back to the inline path",
  },
  QUOTE_ESTIMATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No estimate found on quote data",
  },
};
